import {
  type SortDirection,
  compareNullsLast,
  sortFactor,
} from "~/pages/manifold-nextgen/components/evals/TableSort";
import { CaseOutcome } from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import { ResultSortKey } from "~/pages/manifold-nextgen/components/evals/constants/sorting";
import type { PartialEvalCase, PartialEvalCaseResult } from "~/services/evals";
import { outcomeForResults } from "./cases";
import { resultDurationSeconds } from "./core";

/** One iteration of one case in one run — the row a reviewer opens a transcript from. */
export interface ResultRow {
  result: PartialEvalCaseResult;
  evalCase: PartialEvalCase | undefined;
  outcome: CaseOutcome;
  /** Every iteration of the same case in this run, for the drawer's switcher. */
  siblingResultIds: string[];
}
/**
 * One row per result in a run — an iteration rather than a case, because this is
 * the view where a reviewer opens one transcript. The siblings ride along so the
 * drawer can step between iterations without re-deriving the grouping.
 */
export function buildResultRows(
  results: PartialEvalCaseResult[],
  cases: PartialEvalCase[]
): ResultRow[] {
  const casesById = new Map(cases.map((item) => [item.id, item]));
  const iterationsByCase = new Map<string, string[]>();
  for (const result of [...results].sort((a, b) => a.iteration - b.iteration)) {
    const existing = iterationsByCase.get(result.case_id);
    if (existing) {
      existing.push(result.id);
    } else {
      iterationsByCase.set(result.case_id, [result.id]);
    }
  }

  return results.map((result) => ({
    result,
    evalCase: casesById.get(result.case_id),
    outcome: outcomeForResults([result]).outcome,
    siblingResultIds: iterationsByCase.get(result.case_id) ?? [result.id],
  }));
}
/**
 * Outcome order for sorting, worst first, so an ascending sort puts the rows
 * needing attention on top rather than ordering them alphabetically by label.
 */
const OUTCOME_RANK: Record<CaseOutcome, number> = {
  [CaseOutcome.ERRORED]: 0,
  [CaseOutcome.FAILED]: 1,
  [CaseOutcome.MIXED]: 2,
  [CaseOutcome.PASSED]: 3,
  [CaseOutcome.NOT_RUN]: 4,
};
/** Row order for a run's per-case results table. */
export function sortResultRows(
  rows: ResultRow[],
  key: ResultSortKey,
  direction: SortDirection
): ResultRow[] {
  const factor = sortFactor(direction);
  return [...rows].sort((a, b) => {
    switch (key) {
      case ResultSortKey.OUTCOME:
        return factor * (OUTCOME_RANK[a.outcome] - OUTCOME_RANK[b.outcome]);
      case ResultSortKey.SCORE:
        return compareNullsLast(a.result.score, b.result.score, factor);
      case ResultSortKey.DURATION:
        return compareNullsLast(
          resultDurationSeconds(a.result),
          resultDurationSeconds(b.result),
          factor
        );
      case ResultSortKey.CASE:
      default:
        return (
          factor *
          (a.evalCase?.name ?? a.result.case_id).localeCompare(
            b.evalCase?.name ?? b.result.case_id,
            undefined,
            { sensitivity: "base" }
          )
        );
    }
  });
}
